import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, Paper, Tooltip, IconButton, Divider, useMediaQuery, Card, Chip } from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import { styled } from '@mui/material/styles';
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material';
import { calendarApi, CalendarDayStatusDto, WeekOffRuleDto } from '../../api/calendarApi';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { leaveApi } from '../../api/leaveApi';
import { employeeApi } from '../../api/employeeApi';

const statusColors: Record<string, string> = {
  Present: '#43a047',
  Absent: '#e53935',
  HalfDay: '#ffca28',
  Leave: '#ffb300',
  WeekOff: '#2196f3',
  Holiday: '#ff9800',
  CompOff: '#8e24aa',
  'Days To Come': '#e0e0e0',
};

const DayCell = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'status',
})<{
  status: string;
}>(({ status }) => ({
  backgroundColor: statusColors[status] || statusColors['Days To Come'],
  color: status === 'Days To Come' ? '#888' : '#fff',
  height: 'var(--calendar-cell-size, 54px)',
  width: 'var(--calendar-cell-size, 54px)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  margin: 'var(--calendar-cell-gap, 4px)',
  cursor: 'pointer',
  borderRadius: 'clamp(10px, 22%, 16px)',
  boxShadow: '0 2px 8px rgba(33,150,243,0.07)',
  fontWeight: 600,
  fontSize: 15,
  position: 'relative',
  transition: 'transform 0.1s',
  '&:hover': {
    transform: 'scale(1.07)',
    zIndex: 2,
  },
}));

type CalendarDay = CalendarDayStatusDto & {
  checkInTime?: string;
  checkOutTime?: string;
};

const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const shortMonthNames = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const statusOrder = [
  'Present', 'Absent', 'HalfDay', 'Holiday', 'Leave', 'WeekOff', 'CompOff', 'Days To Come',
];

const overviewStatusOrder = statusOrder.filter((status) => status !== 'Days To Come');

const canApplyWeekOffFallback = (status?: string) =>
  !status || status === 'Absent' || status === 'Days To Come' || status === 'Days to come';

const normalizeMeridiem = (value: string) => value.replace(/\b(am|pm)\b/gi, (match) => match.toUpperCase());

const indianTimeZone = 'Asia/Kolkata';

const timeFormatter = new Intl.DateTimeFormat('en-IN', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: true,
  timeZone: indianTimeZone,
});

const dateFormatter = new Intl.DateTimeFormat('en-IN', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  timeZone: indianTimeZone,
});

const dateTimeFormatter = new Intl.DateTimeFormat('en-IN', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: true,
  timeZone: indianTimeZone,
});

const formatTimeOnlyValue = (hour: number, minute: number) => {
  const period = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;

  return `${String(hour12).padStart(2, '0')}:${String(minute).padStart(2, '0')} ${period}`;
};

type LocalDateTimeParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  hasTimeZone: boolean;
};

const getIsoDateTimeParts = (value: string): LocalDateTimeParts | null => {
  const match = value.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{1,2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?$/i
  );

  if (!match) return null;

  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const second = Number(match[6] || 0);

  if (hour < 0 || hour > 23 || minute < 0 || minute > 59 || second < 0 || second > 59) return null;

  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour,
    minute,
    second,
    hasTimeZone: Boolean(match[7]),
  };
};

const formatLocalDateParts = (parts: Pick<LocalDateTimeParts, 'year' | 'month' | 'day'>) =>
  `${String(parts.day).padStart(2, '0')} ${shortMonthNames[parts.month - 1] || ''} ${parts.year}`;

const formatLocalDateTimeParts = (parts: LocalDateTimeParts) =>
  `${formatLocalDateParts(parts)}, ${formatTimeOnlyValue(parts.hour, parts.minute)}`;

const getMeridiemTimeParts = (value: string) => {
  const match = value.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)$/i);
  if (!match) return null;

  const hour12 = Number(match[1]);
  const minute = Number(match[2]);
  const period = match[3].toUpperCase();

  if (hour12 < 1 || hour12 > 12 || minute < 0 || minute > 59) return null;

  const hour = period === 'AM'
    ? hour12 % 12
    : (hour12 % 12) + 12;

  return { hour, minute };
};

const formatAttendanceTime = (value?: string | null) => {
  const rawValue = (value || '').trim();
  if (!rawValue) return '';

  const meridiemParts = getMeridiemTimeParts(rawValue);
  if (meridiemParts) {
    return formatTimeOnlyValue(meridiemParts.hour, meridiemParts.minute);
  }

  const isoDateTimeParts = getIsoDateTimeParts(rawValue);
  if (isoDateTimeParts && !isoDateTimeParts.hasTimeZone) {
    return formatTimeOnlyValue(isoDateTimeParts.hour, isoDateTimeParts.minute);
  }

  const timeOnlyMatch = rawValue.match(/^(\d{1,2}):(\d{2})(?::\d{2})?/);
  if (timeOnlyMatch) {
    return formatTimeOnlyValue(Number(timeOnlyMatch[1]), Number(timeOnlyMatch[2]));
  }

  const date = new Date(rawValue);
  if (!Number.isNaN(date.getTime())) {
    return normalizeMeridiem(timeFormatter.format(date));
  }

  return rawValue;
};

const formatAttendanceDateTime = (value?: string | null) => {
  const rawValue = (value || '').trim();
  if (!rawValue) return '';

  const isoDateTimeParts = getIsoDateTimeParts(rawValue);
  if (isoDateTimeParts && !isoDateTimeParts.hasTimeZone) {
    return formatLocalDateTimeParts(isoDateTimeParts);
  }

  const date = new Date(rawValue);
  if (!Number.isNaN(date.getTime())) {
    return normalizeMeridiem(dateTimeFormatter.format(date));
  }

  return formatAttendanceTime(rawValue);
};

const getAttendanceTimestamp = (value?: string | null) => {
  const rawValue = (value || '').trim();
  if (!rawValue) return null;

  const meridiemParts = getMeridiemTimeParts(rawValue);
  if (meridiemParts) {
    return meridiemParts.hour * 60 * 60 * 1000 + meridiemParts.minute * 60 * 1000;
  }

  const isoDateTimeParts = getIsoDateTimeParts(rawValue);
  if (isoDateTimeParts && !isoDateTimeParts.hasTimeZone) {
    return Date.UTC(
      isoDateTimeParts.year,
      isoDateTimeParts.month - 1,
      isoDateTimeParts.day,
      isoDateTimeParts.hour,
      isoDateTimeParts.minute,
      isoDateTimeParts.second
    );
  }

  const date = new Date(rawValue);
  if (!Number.isNaN(date.getTime())) return date.getTime();

  const timeOnlyMatch = rawValue.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (!timeOnlyMatch) return null;

  return (
    Number(timeOnlyMatch[1]) * 60 * 60 * 1000 +
    Number(timeOnlyMatch[2]) * 60 * 1000 +
    Number(timeOnlyMatch[3] || 0) * 1000
  );
};

const formatAttendanceTotal = (checkInTime?: string | null, checkOutTime?: string | null) => {
  const checkInTimestamp = getAttendanceTimestamp(checkInTime);
  const checkOutTimestamp = getAttendanceTimestamp(checkOutTime);

  if (checkInTimestamp === null || checkOutTimestamp === null) return '';

  let durationMs = checkOutTimestamp - checkInTimestamp;
  if (durationMs < 0) durationMs += 24 * 60 * 60 * 1000;
  if (durationMs <= 0) return '';

  const totalMinutes = Math.floor(durationMs / (60 * 1000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0 && minutes > 0) return `${hours} hr ${minutes} min`;
  if (hours > 0) return `${hours} hr`;
  return `${minutes} min`;
};

const formatCalendarDate = (value?: string | null) => {
  const rawValue = (value || '').trim();
  const dateOnlyMatch = rawValue.match(/^(\d{4})-(\d{2})-(\d{2})/);

  if (dateOnlyMatch) {
    return dateFormatter.format(
      new Date(Number(dateOnlyMatch[1]), Number(dateOnlyMatch[2]) - 1, Number(dateOnlyMatch[3]))
    );
  }

  const date = new Date(rawValue);
  return Number.isNaN(date.getTime()) ? rawValue : dateFormatter.format(date);
};

const getCalendarDayNumber = (value?: string | null) => {
  const rawValue = (value || '').trim();
  const dateOnlyMatch = rawValue.match(/^\d{4}-\d{2}-(\d{2})/);

  if (dateOnlyMatch) return Number(dateOnlyMatch[1]);

  const date = new Date(rawValue);
  return Number.isNaN(date.getTime()) ? '' : date.getDate();
};

const getAttendanceTooltipTitle = (day: CalendarDay) => {
  const checkIn = formatAttendanceDateTime(day.checkInTime);
  const checkOut = formatAttendanceDateTime(day.checkOutTime);
  const total = formatAttendanceTotal(day.checkInTime, day.checkOutTime);
  const isPresent = day.status === 'Present';
  const hasPunchDetails = isPresent || Boolean(checkIn || checkOut);

  return (
    <Box>
      <Typography variant="caption" display="block" fontWeight={700}>
        {day.status || 'Attendance'}
      </Typography>
      <Typography variant="caption" display="block">
        {isPresent ? 'Present Day' : 'Date'}: {formatCalendarDate(day.date)}
      </Typography>
      {hasPunchDetails && (
        <Typography variant="caption" display="block">
          Check-in: {checkIn || '-'}
        </Typography>
      )}
      {hasPunchDetails && (
        <Typography variant="caption" display="block">
          Check-out: {checkOut || '-'}
        </Typography>
      )}
      {hasPunchDetails && (
        <Typography variant="caption" display="block">
          Total: {total || '-'}
        </Typography>
      )}
    </Box>
  );
};

const LeaveCalendar: React.FC<{ employeeId?: string }> = ({ employeeId }) => {
  const user = useSelector((state: RootState) => state.auth.user);
  const [days, setDays] = useState<CalendarDay[]>([]);
  const [loading, setLoading] = useState(false);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [weekOffRules, setWeekOffRules] = useState<WeekOffRuleDto[]>([]);
  const [leaveBalance, setLeaveBalance] = useState(0);
  const isMobile = useMediaQuery('(max-width:900px)');

  const today = new Date();

  function getWeekOfMonth(date: Date) {
    const day = date.getDate();
    return Math.floor((day - 1) / 7) + 1;
  }

  const calendarDays: CalendarDay[] = [];
  for (let d = 1; d <= new Date(year, month, 0).getDate(); d++) {
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    let dayData = days.find((x) => x.date.startsWith(dateStr));
    const cellDate = new Date(year, month - 1, d);
    const dow = cellDate.getDay();
    const weekOfMonth = getWeekOfMonth(cellDate);
    const backendDayOfWeek = dow === 0 ? 7 : dow;
    const isWeekOff = weekOffRules.some(rule => rule.dayOfWeek === backendDayOfWeek && rule.weekOfMonth === weekOfMonth);
    if (!dayData) {
      if (isWeekOff) {
        dayData = { date: dateStr, status: 'WeekOff' };
      } else if (cellDate > today) {
        dayData = { date: dateStr, status: 'Days To Come' };
      } else {
        dayData = { date: dateStr, status: 'Absent' };
      }
    } else {
      if (cellDate > today && dayData.status === 'Absent') {
        dayData = { ...dayData, status: '' };
      }
      if (isWeekOff && canApplyWeekOffFallback(dayData.status)) {
        dayData = { ...dayData, status: 'WeekOff' };
      }
    }
    calendarDays.push(dayData);
  }

  const statusCounts = calendarDays.reduce<Record<string, number>>((acc, d) => {
    acc[d.status] = (acc[d.status] || 0) + 1;
    return acc;
  }, {});

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
        const endDate = `${year}-${String(month).padStart(2, '0')}-${new Date(year, month, 0).getDate()}`;
        const empId = employeeId || user?.userId;
        const currentEmployee = await employeeApi.getCurrentEmployee();
        const leaveBal = await leaveApi.getLeaveBalance({ employeeId: currentEmployee.id });
        const balance = leaveBal.reduce((acc, lb) => acc + lb.remainingDays, 0);
        setLeaveBalance(balance);
        let mapped: CalendarDay[] = [];
        let weekOff: WeekOffRuleDto[] = [];
        if (empId) {
          const [apiDays, apiWeekOff] = await Promise.all([
            calendarApi.getEmployeeCalendarStatus(empId, startDate, endDate),
            calendarApi.getEmployeeWeekOffRules(empId)
          ]);
          weekOff = apiWeekOff;
          mapped = apiDays.map((d: any) => {
            const checkIn = d.checkInTime ?? d.checkinTime ?? d.check_in_time ?? '';
            const checkOut = d.checkOutTime ?? d.checkoutTime ?? d.check_out_time ?? '';

            return { ...d, checkInTime: checkIn, checkOutTime: checkOut };
          });
        }
        setWeekOffRules(weekOff);
        setDays(mapped);
      } catch {
        setDays([]);
        setWeekOffRules([]);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [employeeId, user?.userId, month, year]);

  // Calendar grid weeks
  const firstDay = new Date(year, month - 1, 1);
  const lastDay = new Date(year, month, 0);
  const startDay = firstDay.getDay();
  const totalDays = lastDay.getDate();
  const weeks: (CalendarDay | undefined)[][] = [];
  let week: (CalendarDay | undefined)[] = Array(startDay).fill(undefined);
  for (let d = 1; d <= totalDays; d++) {
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    let dayData = days.find((x) => x.date.startsWith(dateStr));
    const cellDate = new Date(year, month - 1, d);
    const dow = cellDate.getDay();
    const weekOfMonth = getWeekOfMonth(cellDate);
    const backendDayOfWeek = dow === 0 ? 7 : dow;
    const isWeekOff = weekOffRules.some(rule => rule.dayOfWeek === backendDayOfWeek && rule.weekOfMonth === weekOfMonth);
    if (!dayData) {
      if (isWeekOff) {
        dayData = { date: dateStr, status: 'WeekOff' };
      } else if (cellDate > today) {
        dayData = { date: dateStr, status: 'Days To Come' };
      } else {
        dayData = { date: dateStr, status: 'Absent' };
      }
    } else {
      if (cellDate > today && dayData.status === 'Absent') {
        dayData = { ...dayData, status: '' };
      }
      if (isWeekOff && canApplyWeekOffFallback(dayData.status)) {
        dayData = { ...dayData, status: 'WeekOff' };
      }
    }
    week.push(dayData);
    if (week.length === 7) {
      weeks.push(week);
      week = [];
    }
  }

  if (week.length) {
  // Fill the last row with undefined to complete 7 cells
  while (week.length < 7) {
    week.push(undefined);
  }
  weeks.push(week);
  }
  
  const handlePrevMonth = () => {
    if (month === 1) { setMonth(12); setYear((y) => y - 1); }
    else { setMonth((m) => m - 1); }
  };
  const handleNextMonth = () => {
    if (month === 12) { setMonth(1); setYear((y) => y + 1); }
    else { setMonth((m) => m + 1); }
  };

  const overviewChartData = overviewStatusOrder.map((status, index) => ({
    id: index,
    value: statusCounts[status] || 0,
    label: status,
    color: statusColors[status],
  }));
  const validDays = calendarDays.filter((day) => overviewStatusOrder.includes(day.status));
  const presentDays = validDays.filter((day) => day.status === 'Present').length;
  const attendancePercentage = validDays.length > 0
    ? `${Math.round((presentDays / validDays.length) * 100)}%`
    : '0%';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: 3,
        bgcolor: '#f0f1f5',
        p: isMobile ? 1 : 4,
        borderRadius: 4,
        overflowX: 'hidden',
      }}
    >
      {/* Main Calendar Card */}
      <Card
        sx={{
          flex: 2,
          borderRadius: 4,
          boxShadow: '0 4px 24px #b3c6e022',
          p: { xs: 1.5, sm: 2, md: 3 },
          bgcolor: '#fff',
          minWidth: 0,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {/* ── Header: Title + Month Navigation ── */}
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2.5,
            px: 1.5,
            py: 1.5,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #e3f0ff 0%, #f0f4ff 100%)',
            boxShadow: '0 2px 8px rgba(33,150,243,0.08)',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <Typography
            variant="h5"
            fontWeight={800}
            sx={{ color: '#2e3a59', letterSpacing: 0, fontSize: { xs: 18, sm: 20 } }}
          >
            Attendance Calendar
          </Typography>

          <Box display="flex" alignItems="center" gap={0.5}>
            <IconButton
              onClick={handlePrevMonth}
              size="small"
              sx={{
                bgcolor: '#fff',
                boxShadow: '0 1px 4px #b3c6e033',
                '&:hover': { bgcolor: '#e3f0ff' },
              }}
            >
              <ArrowBackIos fontSize="small" />
            </IconButton>

            <Typography
              variant="h6"
              sx={{
                mx: 1.5,
                minWidth: 170,
                textAlign: 'center',
                fontWeight: 700,
                color: '#2e3a59',
                fontSize: { xs: 15, sm: 17 },
                letterSpacing: 0,
              }}
            >
              {monthNames[month - 1]} {year}
            </Typography>

            <IconButton
              onClick={handleNextMonth}
              size="small"
              sx={{
                bgcolor: '#fff',
                boxShadow: '0 1px 4px #b3c6e033',
                '&:hover': { bgcolor: '#e3f0ff' },
              }}
            >
              <ArrowForwardIos fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* ── Calendar Grid ── */}
        {loading ? (
          <CircularProgress sx={{ mt: 4 }} />
        ) : (
          <>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              width="100%"
              sx={{
                '--calendar-cell-size': {
                  xs: 'clamp(38px, 11.6vw, 54px)',
                  sm: 'clamp(46px, 8vw, 54px)',
                  md: '54px',
                },
                '--calendar-cell-gap': { xs: '2px', sm: '3px', md: '4px' },
                overflowX: 'auto',
                pb: 0.5,
              }}
            >
              {/* Day-of-week headers */}
              <Box display="flex" mb={0.5}>
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                    <Box
                      key={d}
                      sx={{
                        width: 'calc(var(--calendar-cell-size, 54px) + (var(--calendar-cell-gap, 4px) * 2))',
                        height: { xs: 26, sm: 32 },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        color: '#8a8a8a',
                        fontSize: { xs: 11, sm: 13 },
                        flexShrink: 0,
                        letterSpacing: 0,
                      }}
                    >
                      {d}
                    </Box>
                  ))}
                </Box>
              {/* Weeks */}
              {weeks.map((week, i) => (
                <Box key={i} display="flex">
                  {week.map((day, j) =>
                    day ? (
                      <Tooltip
                        key={j}
                        title={getAttendanceTooltipTitle(day)}
                        arrow
                      >
                        <DayCell status={day.status as string}>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 700, fontSize: { xs: 14, sm: 15 }, lineHeight: 1 }}
                          >
                            {getCalendarDayNumber(day.date)}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 700,
                              fontSize: { xs: 8.5, sm: 10 },
                              lineHeight: 1,
                              letterSpacing: 0,
                              maxWidth: 'calc(var(--calendar-cell-size, 54px) - 4px)',
                            }}
                            noWrap
                          >
                            {day.status?.toUpperCase()}
                          </Typography>
                        </DayCell>
                      </Tooltip>
                    ) : (
                      // ✅ Empty cell — must match DayCell total size: 54px + 4px margin each side = 62px wide, 54px + 8px = 62px tall
                      <Box
                        key={j}
                        sx={{
                          width: 'var(--calendar-cell-size, 54px)',
                          height: 'var(--calendar-cell-size, 54px)',
                          margin: 'var(--calendar-cell-gap, 4px)',
                          borderRadius: 'clamp(10px, 22%, 16px)',
                          bgcolor: '#f0f1f5',
                          flexShrink: 0,
                        }}
                      />
                    )
                  )}
                </Box>
              ))}
            </Box>
            {/* ── Attendance Summary Chips ── */}
            <Box
              mt={3}
              width="100%"
              display="flex"
              gap={2}
              flexWrap="wrap"
              justifyContent="center"
              alignItems="center"
              bgcolor="#f6f7fb"
              p={2}
              borderRadius={3}
            >
              <Chip
                icon={
                  <span
                    style={{
                      background: statusColors.Present,
                      borderRadius: '50%',
                      width: 12,
                      height: 12,
                      display: 'inline-block',
                    }}
                  />
                }
                label={`Present Days  ${statusCounts['Present'] || 0}`}
                sx={{ fontWeight: 700, bgcolor: '#e8f5e9', color: '#388e3c', fontSize: 15 }}
              />
              <Chip
                icon={
                  <span
                    style={{
                      background: '#08fc00',
                      borderRadius: '50%',
                      width: 12,
                      height: 12,
                      display: 'inline-block',
                    }}
                  />
                }
                label={`Leave Balance  ${leaveBalance || 0}`}
                sx={{ fontWeight: 700, bgcolor: '#fffde7', color: '#08fc00', fontSize: 15 }}
              />
              <Chip
                icon={
                  <span
                    style={{
                      background: statusColors.WeekOff,
                      borderRadius: '50%',
                      width: 12,
                      height: 12,
                      display: 'inline-block',
                    }}
                  />
                }
                label={`WeekOffs  ${statusCounts['WeekOff'] || 0}`}
                sx={{ fontWeight: 700, bgcolor: '#e3f2fd', color: '#1976d2', fontSize: 15 }}
              />
            </Box>
          </>
        )}
      </Card>

      {/* ── Sidebar ── */}
      <Box sx={{ flex: 1, minWidth: 0, width: isMobile ? '100%' : 'auto', display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Attendance Summary List */}
        <Card sx={{ borderRadius: 4, boxShadow: '0 4px 24px #b3c6e022', p: 2.5, bgcolor: '#fff' }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1, color: '#2e3a59' }}>
            Attendance
          </Typography>
          <Divider sx={{ mb: 1 }} />
          {statusOrder.map((status) => (
            <Box key={status} display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
              <Box display="flex" alignItems="center" gap={1}>
                <Paper
                  sx={{
                    width: 14,
                    height: 14,
                    background: statusColors[status],
                    mr: 0.5,
                    borderRadius: '50%',
                  }}
                />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{status}</Typography>
              </Box>
              <Typography variant="body2">{statusCounts[status] || 0}</Typography>
            </Box>
          ))}
        </Card>

        {/* Attendance Overview Pie Chart */}
        <Card sx={{ borderRadius: 4, boxShadow: '0 4px 24px #b3c6e022', p: 2.5, bgcolor: '#fff', overflow: 'visible' }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1, color: '#2e3a59' }}>
            Attendance Overview
          </Typography>
          <Divider sx={{ mb: 1 }} />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2.5,
              flexWrap: 'wrap',
              pt: 1,
            }}
          >
            <Box sx={{ position: 'relative', width: 180, height: 180, flex: '0 0 180px' }}>
              <PieChart
                hideLegend
                series={[{
                  data: overviewChartData,
                  innerRadius: 40,
                  outerRadius: 60,
                  paddingAngle: 2,
                }]}
                width={180}
                height={180}
                margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                }}
              >
                <Typography variant="h4" fontWeight={800} color="#43a047" sx={{ lineHeight: 1 }}>
                  {attendancePercentage}
                </Typography>
                <Typography variant="caption" color="#888">Attendance</Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                minWidth: 130,
                flex: '0 1 150px',
              }}
            >
              {overviewChartData.map((item) => (
                <Box
                  key={item.label}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.5,
                    minWidth: 0,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        bgcolor: item.color,
                        borderRadius: '50%',
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body2" noWrap>
                      {item.label}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, flexShrink: 0 }}>
                    {item.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Card>
      </Box>
    </Box>
  );
};

export default LeaveCalendar;
